import React, { useContext, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useDispatch } from 'react-redux';
import { useCartPricing } from '../../contexts/CartPricingContext';
import Series3UpgradeContext from '../../contexts/Series3UpgradeContext';
import useProductSkuMap from '../../hooks/useProductSkuMap';
import * as events from '../../lib/analytics/events';
import { isBundleCartItem, isForModuleCartItem, isGiftCardCartItem } from '../../lib/cart';
import { useCartMode } from '../../lib/cartModes';
import CheckoutContext from '../../lib/CheckoutContext';
import { hardwareDiscountPercentOff } from '../../lib/hardwareFirst';
import { colorAndDesignVariantName, colorVariantName, sizeName } from '../../lib/product';
import { centsToDollarsNumber, expectUnreachable, getLocalizedPricing, preciseSum } from '../../lib/util';
import * as types from '../../types';
import {
  CartItemCreditReason,
  isCollarKitProduct,
  isMiniProduct,
  isModuleSubscriptionProduct,
  ProductCategory,
  ISubscriptionProduct,
  ThirdPartyId,
} from '../../types';
import styles from './CartItemList.module.scss';
import GiftCardCartItem from './GiftCardCartItem';
import HardwareFirstCartItem, { HardwareFirstDevice } from '../HardwareFirstCartItem/HardwareFirstCartItem';
import ProductCartItem, { ItemPriceRowProps } from './ProductCartItem';
import { getCartV2CurrencyFormattedTotal } from '../CartSummary/CartActions';
import CartItemV2 from './CartItemV2';
import RescueCartItem from './RescueCartItem';
import { WhatsIncludedItem } from './WhatsIncluded';
import useMakerBandForCartItem from './hooks/useMakerBandForCartItem';
import { showRescuePdpRedesign } from '../../views/RescueLanding/RescueBanner';
import { SERIES_3_UPGRADE_V2_COUPON_CODE } from '../../views/Series3UpgradeV2/Series3UpgradePDPV2';
import { COLLAR_TARIFF_FEE_SKU } from '../../views/ProductDetails/Series3CollarDetails/Series3CollarDetails';
import { getFiGrowthBook } from '../../lib/growthbook';
import AppPaths from '../../AppPaths';
import { useLocale } from '../../contexts/LocaleContext';
import { getFreeTrialDurationLabel } from '../../lib/subscription';
import {
  getPositionDiscountForCartItem,
  calculatePositionDiscountedPrice,
  isEnrolledInPositionDiscount,
} from '../../lib/positionDiscount';
import useFreeTrialShippingPrice from '../../hooks/useFreeTrialShippingPrice';
import useCartItemV2Enabled from '../../hooks/useCartItemV2Enabled';
import useAdoptionPromoApplied from '../../hooks/useAdoptionPromoApplied';
import { ReactComponent as IconWarranty } from '../../assets/images/icons/icon_warranty.svg';

export interface CartItemListProps {
  allowRemoveCartItem?: boolean;
  /** controls the default expanded state of WhatsIncluded in CartItemV2 (default: true) */
  defaultExpanded?: boolean;
}

function sortOrder(category: ProductCategory, t: TFunction) {
  switch (category) {
    case types.ProductCategory.COLLAR_KIT:
    case types.ProductCategory.MINI:
    case types.ProductCategory.ULTRA:
      return 0;
    case types.ProductCategory.BAND:
      return 1;
    case types.ProductCategory.CHARGING_BASE:
      return 3;
    case types.ProductCategory.SUBSCRIPTION:
    case types.ProductCategory.THIRD_PARTY_MEMBERSHIP:
    case types.ProductCategory.EARLY_RENEWAL:
      return 4;
    case types.ProductCategory.ENDLINK:
      return 5;
    case types.ProductCategory.MICROCHIP:
      return 6;
    case types.ProductCategory.GIFT_CARD:
      return 7;
    case types.ProductCategory.S2_UPGRADE:
    case types.ProductCategory.MODULE:
    case types.ProductCategory.MINI_ATTACHMENT:
      return 8;
    case types.ProductCategory.SUPPLEMENT:
      return 9;
    default:
      expectUnreachable(category);
      throw new Error(t('unexpected_product_category', { category }));
  }
}

function useCartItemNameTransformerForCheckoutType() {
  const { checkoutType } = useCartMode();
  const moduleIdToPetNameMap = useContext(Series3UpgradeContext)?.moduleIdToPetNameMap;

  if (checkoutType !== types.CheckoutType.Upgrade || !moduleIdToPetNameMap) {
    return (originalName: string) => originalName;
  }

  return (originalName: string, cartItem: types.CartItem) => {
    if (isForModuleCartItem(cartItem)) {
      const petName = moduleIdToPetNameMap.get(cartItem.forModuleId);
      if (petName) {
        return `${petName}'s ${originalName}`;
      }
    }

    return originalName;
  };
}

function descriptionFromCreditReason(creditReason: CartItemCreditReason, isUpgradeV2: boolean, t: TFunction) {
  if (isUpgradeV2) {
    return t('cyber_monday_offer');
  }

  switch (creditReason) {
    case 'upgrade':
      return t('series_2_credit');
    case 'promo':
    case 'promo_waived_activation_fee':
      return t('discount');
    case 'household_discount_five_percent':
      return t('multi_dog_discount_5_percent');
    case 'household_discount_ten_percent':
      return t('multi_dog_discount_10_percent');
    case 'free_trial':
      return t('months_free');
    default:
      expectUnreachable(creditReason);
      throw new Error('Unexpected credit reason');
  }
}

/**
 * Returns a localized subscription product name. For prepaid subscriptions (billingPeriodsPerTerm === 1,
 * renewalMonths > 1), uses the `prepaid_membership_months` translation key instead of the hardcoded
 * English name from the backend API. For multi-period commitments, appends the total term length.
 */
function productNameWithSubscriptionCommitment(product: types.IProduct, t: TFunction) {
  const subscriptionProductName = product.name;

  if (!isModuleSubscriptionProduct(product)) {
    return subscriptionProductName;
  }

  // Prepaid subscriptions (e.g. "6 month prepaid membership") — use translation key
  if (product.billingPeriodsPerTerm === 1) {
    if (product.renewalMonths > 1 && !product.buyItMembership) {
      return t('prepaid_membership_months', { months: product.renewalMonths });
    }
    return subscriptionProductName;
  }

  const totalTermLengthInMonths = product.billingPeriodsPerTerm * product.renewalMonths;
  if (totalTermLengthInMonths % 12 === 0) {
    const years = Math.round(totalTermLengthInMonths / 12);
    return t('subscription_commitment_years', { productName: subscriptionProductName, years });
  }

  return subscriptionProductName;
}

function complexProductVariantDescription(variant: types.IVariant, bandSeries: types.BandSeries) {
  const descriptionParts = [colorVariantName(variant.options.color, bandSeries)];
  descriptionParts.push(sizeName(variant.options.size));

  return descriptionParts.join(', ');
}

function longProductVariantDescription(variant: types.IVariant, bandSeries: types.BandSeries) {
  return `${sizeName(variant.options.size)}, ${colorVariantName(variant.options.color, bandSeries)}`;
}

export function checkoutImageUrlForProduct(productId: string) {
  if (productId === 'supplements-multivitamin') {
    return '/product_images/supplements/upsell-v2/formula-bag.png';
  } else if (productId === 'supplements-skin-coat') {
    return '/product_images/supplements/upsell-v2/skincoat-bag.png';
  } else if (productId === 'supplements-hip-joint') {
    return '/product_images/supplements/upsell-v2/hipjoint-bag.png';
  } else if (productId === 'supplements-calming') {
    return '/product_images/supplements/upsell-v2/calming-bag.png';
  } else if (productId === 'smart-collar-mini') {
    return '/product_images/smart-collar-mini.png';
  } else if (productId === 'smart-collar-ultra') {
    return '/product_images/smart-collar-ultra.png';
  }
  return `/product_images/${productId}.jpg`;
}

export function checkoutImageUrlForProductWithVariant(productId: string, variant: types.IVariant) {
  return `/product_images/${productId}/${variant.options.color.replace(/[\s/]/g, '_')}.png`;
}

function buildWhatsIncludedItems(
  variant: types.IVariant,
  bandSeries: types.BandSeries,
  t: TFunction,
  isFreeTrialItem?: boolean,
): WhatsIncludedItem[] {
  const colorName = colorVariantName(variant.options.color, bandSeries);
  const size = sizeName(variant.options.size);
  const colorForImagePath = variant.options.color.replace(/[\s/]/g, '_');

  return [
    {
      imageUrl: `/product_images/smart-collar-v3b/${colorForImagePath}.png`,
      name: t('cart_item_v2.series_3_device'),
      description: t('cart_item_v2.s3_device_description'),
      zoomImage: true,
    },
    {
      imageUrl: `/product_images/additional-band-s3/${colorForImagePath}.png`,
      name: t('cart_item_v2.series_3_band'),
      description: `${colorName}, ${size}`,
    },
    {
      imageUrl: '/product_images/additional-base-s3.png',
      name: t('cart_item_v2.series_3_base'),
      description: t('cart_item_v2.base_description'),
    },
    {
      icon: IconWarranty,
      name: t('cart_item_v2.lifetime_warranty'),
      description: t('cart_item_v2.warranty_description'),
    },
  ];
}

function buildMiniWhatsIncludedItems(t: TFunction): WhatsIncludedItem[] {
  return [
    {
      imageUrl: '/product_images/mini/device.png',
      name: t('cart_item_v2.mini_device'),
      description: t('cart_item_v2.mini_device_description'),
    },
    {
      imageUrl: '/product_images/mini/unboxing.png',
      name: t('cart_item_v2.mini_accessories'),
      description: t('cart_item_v2.mini_accessories_description'),
    },
    {
      icon: IconWarranty,
      name: t('cart_item_v2.lifetime_warranty'),
      description: t('cart_item_v2.warranty_description'),
    },
  ];
}

/** Which hardware-first card a repriced collar renders as; undefined for every other product. */
function hardwareFirstDevice(product: types.IProduct): HardwareFirstDevice | undefined {
  if (isMiniProduct(product)) {
    return 'mini';
  }
  if (product.id === types.ultraCollarId) {
    return 'ultra';
  }
  return isCollarKitProduct(product) ? 'series3' : undefined;
}

function cartItemV2Title(product: types.IProduct, isFreeTrialItem: boolean, t: TFunction): string {
  const productLabel = isCollarKitProduct(product)
    ? t('cart_item_v2.series_3_plus')
    : isMiniProduct(product)
      ? t('cart_item_v2.mini')
      : product.name;

  return isFreeTrialItem
    ? t('cart_item_v2.trial_membership_title', { product: productLabel })
    : t('cart_item_v2.membership_title', { product: productLabel });
}

function cartItemV2Subtitle(subscriptionProduct: ISubscriptionProduct, t: TFunction): string {
  if (subscriptionProduct.freeTrial && subscriptionProduct.freeTrialDays) {
    const months = Math.round(subscriptionProduct.freeTrialDays / 30);
    return t('cart_item_v2.free_trial_subtitle', { days: months });
  }
  const totalMonths = subscriptionProduct.billingPeriodsPerTerm * subscriptionProduct.renewalMonths;
  const { renewalMonths } = subscriptionProduct;
  if (renewalMonths === 1) return t('cart_item_v2.plan_subtitle_monthly', { months: totalMonths });
  if (renewalMonths === 3) return t('cart_item_v2.plan_subtitle_quarterly', { months: totalMonths });
  if (renewalMonths === 6) return t('cart_item_v2.plan_subtitle_semi_annually');
  if (renewalMonths === 12) return t('cart_item_v2.plan_subtitle_annually');
  if (renewalMonths === 24) return t('cart_item_v2.plan_subtitle_biennially');
  return t('cart_item_v2.plan_subtitle_monthly', { months: totalMonths });
}

interface UsePriceRowsProps {
  bundledSubscriptionProduct?: types.ISubscriptionProduct;
  cartItemId: string;
  makerBand?: types.MakerBandDetails;
  pricingInCents: types.PricingByCurrency;
  productName: string;
  variantDescription?: string;
  isUpgradeV2: boolean;
  addonSkus?: string[];
  productUrl?: string;
}

function usePriceRows(): (props: UsePriceRowsProps) => ItemPriceRowProps[] {
  const cartPricing = useCartPricing();
  const isVerizon = useCartMode().checkoutType === types.CheckoutType.Verizon;
  const { t } = useTranslation();
  const { currency } = useLocale();

  return useCallback(
    ({
      bundledSubscriptionProduct,
      cartItemId,
      makerBand,
      pricingInCents,
      productName,
      variantDescription,
      isUpgradeV2,
      addonSkus,
      productUrl,
    }) => {
      const bundledSubscriptionProductPrice =
        bundledSubscriptionProduct && !bundledSubscriptionProduct.freeTrial
          ? getLocalizedPricing(bundledSubscriptionProduct.pricingInCents, currency)
          : 0;
      const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
      const isSeries3Plus = productName === t('fi_series_3_plus');
      const isMini = productName === 'Fi Mini';
      const isUltra = productName === 'Fi Ultra';
      const showPriceOnSubscriptionRow = showEcomCartTest && (isMini || isSeries3Plus || isUltra);
      const isVerizonMini = isVerizon && isMini;

      const priceInCents = getLocalizedPricing(pricingInCents, currency);

      // Hardware-first repriced item (standalone collar): the server's per-unit price replaces the
      // list price, with the list price struck through
      const hardwareDiscount = !bundledSubscriptionProduct ? cartPricing.hardwareDiscounts?.[cartItemId] : undefined;
      const hardwareStrikethroughInCents =
        hardwareDiscount && hardwareDiscount.listPriceInCents > hardwareDiscount.discountedPriceInCents
          ? hardwareDiscount.listPriceInCents
          : undefined;

      let mainPriceRowTotalPriceInCents =
        !showPriceOnSubscriptionRow && !isUltra
          ? preciseSum([priceInCents, bundledSubscriptionProductPrice])
          : undefined;

      if (isVerizonMini) {
        mainPriceRowTotalPriceInCents = priceInCents;
      }

      if (hardwareDiscount && !isUltra) {
        mainPriceRowTotalPriceInCents = hardwareDiscount.discountedPriceInCents;
      }

      const mainRowProductName = showEcomCartTest && isSeries3Plus ? t('series_3_plus') : productName;

      const priceRows: ItemPriceRowProps[] = [
        {
          productName: mainRowProductName,
          totalPriceInCents: mainPriceRowTotalPriceInCents,
          discountedFromOriginalPriceInCents: !isUltra ? hardwareStrikethroughInCents : undefined,
          // If this is a maker band, we show the variant descirption on a separate pricing row
          variantDescription: makerBand ? undefined : variantDescription,
          productUrl,
        },
      ];

      // For Ultra, add "Hardware kit" as a secondary row with the hardware price
      if (isUltra) {
        priceRows.push({
          productName: t('hardware_kit'),
          totalPriceInCents: hardwareDiscount?.discountedPriceInCents ?? priceInCents,
          discountedFromOriginalPriceInCents: hardwareStrikethroughInCents,
          secondary: true,
        });
      }

      if (bundledSubscriptionProduct) {
        // Check for position-based discount (new multi-pet experiment)
        const positionDiscount = getPositionDiscountForCartItem(cartPricing, cartItemId);
        const isEnrolledInExperiment = isEnrolledInPositionDiscount(cartPricing);

        // Only fall back to legacy multidog if NOT enrolled in position discount experiment
        const multiDogDiscount =
          !positionDiscount && !isEnrolledInExperiment
            ? cartPricing.cartItemCredits?.[cartItemId]?.find(
                (c) => c.creditReason === 'household_discount_five_percent',
              )
            : undefined;
        const multiDogDiscountAmount = multiDogDiscount?.creditAmountInCents ?? 0;

        // Only show a split out price for free trial subscriptions. For non-free trial subscriptions, we've
        // already bundled the subscription price into the total price in the primary row.
        let totalPriceInCents = bundledSubscriptionProduct.freeTrial
          ? getLocalizedPricing(bundledSubscriptionProduct.pricingInCents, currency)
          : undefined;
        if (showPriceOnSubscriptionRow) {
          totalPriceInCents = preciseSum([priceInCents, bundledSubscriptionProductPrice]);
        }
        // For Ultra, show subscription price separately
        if (isUltra) {
          totalPriceInCents = bundledSubscriptionProductPrice;
        }

        // There are 2 scenarios where we want to show the discount on the line item as a strikethru.
        // 1. If the entire line item is $0 (e.g. GwP coupon)
        // 2. If the discount is recurring (e.g. multi-dog discount or position discount)
        //
        // For other discounts, in this design, it will show on the total
        let discountedPrice: number;
        if (positionDiscount) {
          // Use position discount percentage to calculate discounted price
          discountedPrice = calculatePositionDiscountedPrice(totalPriceInCents ?? 0, positionDiscount.discountPercent);
        } else {
          discountedPrice = Math.max((totalPriceInCents ?? 0) - multiDogDiscountAmount, 0);
        }
        const totalCredits = preciseSum(
          cartPricing.cartItemCredits?.[cartItemId]?.map((credit) => credit.creditAmountInCents) ?? [],
        );
        if (totalCredits === totalPriceInCents) {
          discountedPrice = totalPriceInCents - totalCredits;
        }
        let secondaryProductName = productNameWithSubscriptionCommitment(bundledSubscriptionProduct, t);

        // For free trial subscriptions, show "X Month Free Trial (refurbished)"
        if (bundledSubscriptionProduct.freeTrial && bundledSubscriptionProduct.freeTrialDays) {
          secondaryProductName = t('free_trial_title', {
            duration: getFreeTrialDurationLabel(bundledSubscriptionProduct.freeTrialDays),
          });
        }

        // For Ultra, show "X month prepaid membership" format
        if (isUltra && isModuleSubscriptionProduct(bundledSubscriptionProduct)) {
          const totalMonths =
            bundledSubscriptionProduct.billingPeriodsPerTerm * bundledSubscriptionProduct.renewalMonths;
          secondaryProductName = t('prepaid_membership_months', { months: totalMonths });
        }

        let subVariantDescription: string | undefined =
          bundledSubscriptionProduct?.renewalMonths === 1 || bundledSubscriptionProduct?.renewalMonths === 3
            ? t('six_months_minimum')
            : undefined;

        let discountedFromOriginalPriceInCents = discountedPrice === totalPriceInCents ? undefined : totalPriceInCents;

        // For Verizon subscription row:
        //   - No note about the min term
        //   - Show special name
        //   - No strikethrough
        if (isVerizonMini) {
          discountedPrice = bundledSubscriptionProduct.priceInCents;
          secondaryProductName = t('verizon_membership_monthly');
          subVariantDescription = undefined;
          discountedFromOriginalPriceInCents = undefined;
        }

        // For Ultra, no variant description needed
        if (isUltra) {
          subVariantDescription = undefined;
        }

        priceRows.push({
          productName: secondaryProductName,
          secondary: true,
          // For free trial: don't show "Free for X days", show $0 instead
          freeTrialLengthInDays: bundledSubscriptionProduct.freeTrial
            ? undefined
            : bundledSubscriptionProduct.freeTrialDays,
          totalPriceInCents: bundledSubscriptionProduct.freeTrial ? 0 : discountedPrice,
          variantDescription: subVariantDescription,
          discountedFromOriginalPriceInCents,
          isFreeTrialRow: bundledSubscriptionProduct.freeTrial,
        });
      }

      if (makerBand) {
        priceRows.push({
          productName: makerBand.bandMakerName,
          variantDescription,
          totalPriceInCents: makerBand.bandPriceInCents,
          secondary: showEcomCartTest,
        });
      }

      // Check if enrolled in position discount experiment (for filtering legacy credits)
      const isEnrolledInExperimentForCredits = isEnrolledInPositionDiscount(cartPricing);

      // Position discount experiment does NOT waive activation fees - only subscriptions are discounted
      let waiveActivationFee = cartPricing.activationFeesInCents === 0;

      if (cartPricing.cartItemCredits?.[cartItemId] && !showEcomCartTest) {
        // Filter out legacy multidog credits when enrolled in position discount experiment
        const creditsToShow = isEnrolledInExperimentForCredits
          ? cartPricing.cartItemCredits[cartItemId].filter(
              (credit) =>
                credit.creditReason !== 'household_discount_five_percent' &&
                credit.creditReason !== 'household_discount_ten_percent',
            )
          : cartPricing.cartItemCredits[cartItemId];

        priceRows.push(
          ...creditsToShow.map((credit) => {
            // Only update waiveActivationFee if it's currently false
            waiveActivationFee =
              waiveActivationFee ||
              ['household_discount_five_percent', 'household_discount_ten_percent'].includes(credit.creditReason);

            // Calculate the correct discount amount to display for the household discount credits since line item
            // discounts are bundled across potentially multiple coupons.
            const discountAmount =
              credit.creditReason === 'household_discount_five_percent'
                ? 0.05 * bundledSubscriptionProductPrice
                : credit.creditReason === 'household_discount_ten_percent'
                  ? 0.1 * bundledSubscriptionProductPrice
                  : credit.creditAmountInCents;
            return {
              productName: descriptionFromCreditReason(credit.creditReason, isUpgradeV2, t),
              totalPriceInCents: -discountAmount,
            };
          }),
        );
      }

      if (addonSkus) {
        addonSkus.forEach((sku) => {
          if (sku === COLLAR_TARIFF_FEE_SKU) {
            priceRows.push({
              productName: t('tariff_fee'),
              totalPriceInCents: 5000,
            });
          }
        });
      }

      if (bundledSubscriptionProduct) {
        const bundleActivationFee =
          bundledSubscriptionProduct.activationFeePricingInCents !== undefined
            ? getLocalizedPricing(bundledSubscriptionProduct.activationFeePricingInCents, currency)
            : undefined;
        const activationFeeInCents = waiveActivationFee ? 0 : bundleActivationFee;
        // TODO: figure this one out
        const discountedFromOriginalPriceInCents = isUpgradeV2
          ? 2000
          : waiveActivationFee
            ? bundleActivationFee
            : bundledSubscriptionProduct.discountedFromOriginalActivationFeeInCents;
        const activationFeeProductName = isVerizon ? t('verizon_fee_line_activation') : t('one_time_activation_fee');

        if (activationFeeInCents || discountedFromOriginalPriceInCents) {
          priceRows.push({
            productName: activationFeeProductName,
            totalPriceInCents: activationFeeInCents,
            discountedFromOriginalPriceInCents,
            secondary: showEcomCartTest,
          });
        }
      }

      return priceRows;
    },
    [cartPricing, currency, t, isVerizon],
  );
}

function useProductName(): (cartItem: types.CartItem) => string {
  const productSkuMap = useProductSkuMap();
  const maybeTransformCartItemName = useCartItemNameTransformerForCheckoutType();
  const { t } = useTranslation();

  return useCallback(
    (cartItem: types.CartItem) => {
      const { product } = productSkuMap.get(cartItem.lineItem.sku) ?? {};
      if (!product) {
        throw new Error(t('invalid_cart_item'));
      }

      if (product.category === types.ProductCategory.BAND || product.category === types.ProductCategory.COLLAR_KIT) {
        return maybeTransformCartItemName(product.name, cartItem);
      }

      return productNameWithSubscriptionCommitment(product, t);
    },
    [maybeTransformCartItemName, productSkuMap, t],
  );
}

function useVariantDescription(): (cartItem: types.CartItem) => string | undefined {
  const productSkuMap = useProductSkuMap();
  const { t } = useTranslation();

  return useCallback(
    (cartItem: types.CartItem) => {
      if (cartItem.lineItem.sku === 'F1-N-20') {
        return t('chips_per_box', { count: 20 });
      }

      const { product, variant } = productSkuMap.get(cartItem.lineItem.sku) ?? {};
      if (product?.category !== types.ProductCategory.BAND && product?.category !== types.ProductCategory.COLLAR_KIT) {
        return;
      }

      if (!variant) {
        throw new Error(t('invalid_cart_item'));
      }

      const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
      if (showEcomCartTest) {
        return longProductVariantDescription(variant, product.bandSeries);
      }
      return complexProductVariantDescription(variant, product.bandSeries);
    },
    [productSkuMap, t],
  );
}

const pdpURLForProduct = (productId: string) => {
  if (productId === 'smart-collar-mini') {
    return AppPaths.Products('fi-mini');
  } else if (productId === 'smart-collar-ultra') {
    return AppPaths.Products('fi-ultra');
  } else if (
    productId === 'supplements-multivitamin' ||
    productId === 'supplements-skin-coat' ||
    productId === 'supplements-hip-joint' ||
    productId === 'supplements-calming'
  ) {
    return AppPaths.Products('supplements');
  }
  return AppPaths.Products(productId);
};

/**
 * Group the cart items by product type for rendering.
 *
 * @returns cartItemsByGroup - an object with keys of the product type and values of React components.
 * @returns invalidCartItemIds - ids of cart items that contain invalid products.
 */
function useCartItemsByGroup(allowRemoveCartItem?: boolean, defaultExpanded?: boolean) {
  const dispatch = useDispatch();
  const makerBandForCartItem = useMakerBandForCartItem();
  const productSkuMap = useProductSkuMap();
  const priceRowsForCartItem = usePriceRows();
  const productNameForCartItem = useProductName();
  const variantDescriptionForCartItem = useVariantDescription();
  const { cart, checkoutActions } = useContext(CheckoutContext);
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const isVerizon = useCartMode().checkoutType === types.CheckoutType.Verizon;
  const { t } = useTranslation();
  const { currency, currencyFormatter, wholeNumberCurrencyFormatter } = useLocale();
  const freeTrialShippingPrice = useFreeTrialShippingPrice();
  const cartItemV2Enabled = useCartItemV2Enabled();
  const cartPricing = useCartPricing();
  const adoptionPromoApplied = useAdoptionPromoApplied();

  return useMemo(() => {
    const cartItemsByGroup = new Map<ProductCategory, React.ReactNode[]>();
    const invalidCartItemIds: types.CartItemID[] = [];

    const addCartItemDetailsToGroup = (renderGroup: ProductCategory, cartItemDetails: React.ReactNode) => {
      const row = cartItemsByGroup.get(renderGroup) ?? [];
      row.push(cartItemDetails);
      cartItemsByGroup.set(renderGroup, row);
    };

    let hardwareFirstItemsRendered = 0;

    for (const cartItem of Object.values(cart.cartItems)) {
      const removeCartItem = allowRemoveCartItem
        ? () => {
            events.cartItemRemoved({ cartItem, currency });
            dispatch(checkoutActions.removeCartItem({ id: cartItem.cartItemId }));
          }
        : undefined;

      try {
        const { product, variant } = productSkuMap.get(cartItem.lineItem.sku) ?? {};
        if (!product) {
          throw new Error(t('invalid_cart_item'));
        }

        let bundledSubscriptionProduct: ISubscriptionProduct | undefined;
        let addonSkus: string[] | undefined;
        if (isBundleCartItem(cartItem)) {
          const subscriptionLineItemProduct = productSkuMap.get(cartItem.subscriptionLineItem.sku)?.product;
          if (!subscriptionLineItemProduct || !isModuleSubscriptionProduct(subscriptionLineItemProduct)) {
            throw new Error(t('invalid_cart_item'));
          }

          bundledSubscriptionProduct = subscriptionLineItemProduct;
          addonSkus = cartItem.addonLineItems?.map((lineItem) => lineItem.sku);
        }

        if (product.category === types.ProductCategory.GIFT_CARD) {
          if (!isGiftCardCartItem(cartItem)) {
            throw new Error(t('invalid_cart_item'));
          }

          addCartItemDetailsToGroup(
            product.category,
            <GiftCardCartItem
              giftCardDelivery={cartItem.giftCardDelivery}
              id={cartItem.cartItemId}
              key={cartItem.cartItemId}
              productPriceInCents={getLocalizedPricing(product.pricingInCents, currency)}
              removeCartItem={removeCartItem}
              currencyFormatter={currencyFormatter}
            />,
          );
        } else {
          const imageUrl = variant
            ? checkoutImageUrlForProductWithVariant(product.id, variant)
            : checkoutImageUrlForProduct(product.id);

          const makerBand = makerBandForCartItem(cartItem);
          const productName = productNameForCartItem(cartItem);
          const variantDescription = variantDescriptionForCartItem(cartItem);
          const priceRows = priceRowsForCartItem({
            bundledSubscriptionProduct,
            cartItemId: cartItem.cartItemId,
            makerBand,
            pricingInCents: isVerizon
              ? { USD: product.thirdPartyPriceInCents?.[ThirdPartyId.VERIZON] ?? product.pricingInCents.USD }
              : product.pricingInCents,
            productName: productName,
            variantDescription,
            isUpgradeV2: cart.couponCode === SERIES_3_UPGRADE_V2_COUPON_CODE,
            addonSkus,
          });

          const isFreeTrialItem = bundledSubscriptionProduct?.freeTrial === true;
          const freeTrialSubscriptionPrice =
            isFreeTrialItem && bundledSubscriptionProduct
              ? currencyFormatter.format(
                  centsToDollarsNumber(getLocalizedPricing(bundledSubscriptionProduct.pricingInCents, currency)),
                )
              : undefined;

          const isUltraStandalone = !bundledSubscriptionProduct && product.id === types.ultraCollarId;

          const hardwareDiscount = !bundledSubscriptionProduct
            ? cartPricing.hardwareDiscounts?.[cartItem.cartItemId]
            : undefined;
          // a row added from the cross-sell is not in the pricing snapshot the provider is still
          // holding, so keying the card on its own discount dropped it to the generic item until the
          // refetch landed. the presence of any discounts says the arm is on; the card renders on
          // that, and the price fills in when this row's own figures arrive
          const hardwareFirstCardDevice =
            cartPricing.hardwareDiscounts && !bundledSubscriptionProduct ? hardwareFirstDevice(product) : undefined;

          if (hardwareFirstCardDevice) {
            // hardware prices are whole dollars on this surface, so the ".00" is noise
            const formatHardwarePrice = (cents: number) =>
              currencyFormatter.format(centsToDollarsNumber(cents)).replace(/\.00$/, '');
            // the design badges every tracker after the first as the discounted "additional" one.
            // the rate comes off this item's own prices rather than the unit count, and rounds down
            // where the server's `discountPercent` rounds to nearest
            const additionalTrackerPercentOff =
              hardwareFirstItemsRendered > 0 && hardwareDiscount
                ? hardwareDiscountPercentOff(hardwareDiscount.listPriceInCents, hardwareDiscount.discountedPriceInCents)
                : undefined;
            hardwareFirstItemsRendered += 1;

            addCartItemDetailsToGroup(
              product.category,
              <HardwareFirstCartItem
                additionalTrackerPercentOff={additionalTrackerPercentOff}
                device={hardwareFirstCardDevice}
                key={cartItem.cartItemId}
                listPrice={
                  hardwareDiscount && hardwareDiscount.listPriceInCents > hardwareDiscount.discountedPriceInCents
                    ? formatHardwarePrice(hardwareDiscount.listPriceInCents)
                    : undefined
                }
                price={hardwareDiscount ? formatHardwarePrice(hardwareDiscount.discountedPriceInCents) : undefined}
                productImageUrl={imageUrl}
                productName={productName}
                productUrl={pdpURLForProduct(product.id)}
                removeCartItem={removeCartItem}
                variantDescription={
                  variant && isCollarKitProduct(product)
                    ? longProductVariantDescription(variant, product.bandSeries)
                    : undefined
                }
              />,
            );
          } else if (cartItemV2Enabled && bundledSubscriptionProduct) {
            const isMini = isMiniProduct(product);
            const isUltraItem = product.id === types.ultraCollarId;
            const isCollarBundleItem = isMini || isCollarKitProduct(product) || isUltraItem;
            const useItemizedLayout = isCollarBundleItem && !isFreeTrialItem;

            // Ultra prices are whole-dollar — render them without the trailing ".00" to match the
            // PDP styling. Other products keep their cents.
            const formatItemPrice = (cents: number) => {
              const formatted = currencyFormatter.format(centsToDollarsNumber(cents));
              return isUltraItem ? formatted.replace(/\.00$/, '') : formatted;
            };

            // the rescue rows drop trailing zeroes ("$0", "$99") and leave the cents to the totals
            // block below, where the figures have to be seen to add up
            const formatRescueRowPrice = (cents: number) =>
              getCartV2CurrencyFormattedTotal(cents, currencyFormatter, wholeNumberCurrencyFormatter);

            const includedItems = isMini
              ? buildMiniWhatsIncludedItems(t)
              : variant && isCollarKitProduct(product)
                ? buildWhatsIncludedItems(variant, product.bandSeries, t, isFreeTrialItem)
                : undefined;

            const productLabel = isCollarKitProduct(product)
              ? t('cart_item_v2.series_3_plus')
              : isMini
                ? t('cart_item_v2.mini')
                : isUltraItem
                  ? t('fi_ultra')
                  : product.name;

            const title = useItemizedLayout ? productLabel : cartItemV2Title(product, isFreeTrialItem, t);
            const subtitle = useItemizedLayout ? undefined : cartItemV2Subtitle(bundledSubscriptionProduct, t);

            const activationFeeInCents = bundledSubscriptionProduct.activationFeePricingInCents
              ? getLocalizedPricing(bundledSubscriptionProduct.activationFeePricingInCents, currency)
              : 0;
            const activationFeeWaived =
              !isFreeTrialItem && activationFeeInCents > 0 && (cartPricing.waivedActivationFeesInCents ?? 0) > 0;
            const activationFee =
              !isFreeTrialItem && activationFeeInCents > 0 ? formatItemPrice(activationFeeInCents) : undefined;

            // Verizon customers pay a third-party kit price for hardware that is $0 in the D2C bundle,
            // so the itemized kit line must use the Verizon price instead of showing "Free".
            const hardwarePricingInCents = isVerizon
              ? { USD: product.thirdPartyPriceInCents?.[ThirdPartyId.VERIZON] ?? product.pricingInCents.USD }
              : product.pricingInCents;
            const hardwareKitInCents = isUltraItem
              ? getLocalizedPricing((product as types.IUltraProduct).standalonePricingInCents, currency)
              : getLocalizedPricing(hardwarePricingInCents, currency);
            const hardwareKitPrice = useItemizedLayout
              ? hardwareKitInCents === 0
                ? t('cart_item_v2.price_free')
                : formatItemPrice(hardwareKitInCents)
              : isUltraItem
                ? formatItemPrice(hardwareKitInCents)
                : undefined;

            // Ultra's hardware kit is discounted when bundled with a new membership: the standalone
            // (bundled) price is shown, struck through from the regular hardware price (pricingInCents).
            const hardwareKitRegularInCents = isUltraItem ? getLocalizedPricing(product.pricingInCents, currency) : 0;
            const hardwareKitOriginalPrice =
              useItemizedLayout && isUltraItem && hardwareKitRegularInCents > hardwareKitInCents
                ? formatItemPrice(hardwareKitRegularInCents)
                : undefined;
            const hardwareKitDiscountLabel = hardwareKitOriginalPrice ? t('new_membership_discount') : undefined;

            const membershipPriceCents = getLocalizedPricing(bundledSubscriptionProduct.pricingInCents, currency);
            const membershipName = useItemizedLayout
              ? productNameWithSubscriptionCommitment(bundledSubscriptionProduct, t)
              : undefined;

            const hardwareInBundleCents = isUltraItem ? 0 : getLocalizedPricing(hardwarePricingInCents, currency);
            const totalPriceInCents = isFreeTrialItem ? 0 : hardwareInBundleCents + membershipPriceCents;

            // check for cart item credits (e.g. multi-dog discount) to show strikethrough
            // exclude activation fee waivers — those are displayed separately via activationFeeWaived
            const itemCredits = cartPricing.cartItemCredits?.[cartItem.cartItemId] ?? [];
            const priceCredits = itemCredits.filter((c) => c.creditReason !== 'promo_waived_activation_fee');
            const itemCreditTotal = preciseSum(priceCredits.map((c) => c.creditAmountInCents));
            const discountedPriceInCents = totalPriceInCents - itemCreditTotal;
            const hasDiscount = !isFreeTrialItem && itemCreditTotal > 0;

            // In the itemized layout the recurring multi-dog discount lands on the membership line, so show the
            // discounted membership price with the original struck through (the discount persists for as long as
            // both collars stay active).
            const membershipPrice = useItemizedLayout
              ? formatItemPrice(hasDiscount ? membershipPriceCents - itemCreditTotal : membershipPriceCents)
              : undefined;
            const membershipOriginalPrice =
              useItemizedLayout && hasDiscount ? formatItemPrice(membershipPriceCents) : undefined;

            const price = useItemizedLayout
              ? undefined
              : totalPriceInCents === 0
                ? t('cart_item_v2.price_free')
                : hasDiscount
                  ? currencyFormatter.format(centsToDollarsNumber(discountedPriceInCents))
                  : currencyFormatter.format(centsToDollarsNumber(totalPriceInCents));
            const originalPrice =
              !useItemizedLayout && hasDiscount
                ? currencyFormatter.format(centsToDollarsNumber(totalPriceInCents))
                : undefined;

            // the rescue cart states the membership as its own line item and moves the activation
            // fee into the totals block, so it gets its own row renderer rather than a pile of
            // flags on the card every other cart uses
            if (showRescuePdpRedesign() && useItemizedLayout && !isFreeTrialItem) {
              addCartItemDetailsToGroup(
                product.category,
                <RescueCartItem
                  cartItemId={cartItem.cartItemId}
                  key={cartItem.cartItemId}
                  productImageUrl={imageUrl}
                  title={isMini ? t('fi_mini') : productLabel}
                  subtitle={variant ? colorAndDesignVariantName(variant.options.color) : undefined}
                  price={formatRescueRowPrice(hardwareKitInCents)}
                  membershipName={membershipName ?? productLabel}
                  membershipPrice={formatRescueRowPrice(membershipPriceCents - itemCreditTotal)}
                  membershipOriginalPrice={hasDiscount ? formatRescueRowPrice(membershipPriceCents) : undefined}
                  // credits the shelter for the membership, so it may only appear once the discount
                  // is really on the cart - otherwise it reads as free next to a price they will pay
                  membershipNote={adoptionPromoApplied ? t('included_with_your_adoption') : undefined}
                  removeCartItem={removeCartItem}
                />,
              );
              continue;
            }

            addCartItemDetailsToGroup(
              product.category,
              <CartItemV2
                cartItemId={cartItem.cartItemId}
                key={cartItem.cartItemId}
                productImageUrl={imageUrl}
                title={title}
                subtitle={subtitle}
                price={price}
                originalPrice={originalPrice}
                hardwareKitPrice={hardwareKitPrice}
                hardwareKitOriginalPrice={hardwareKitOriginalPrice}
                hardwareKitDiscountLabel={hardwareKitDiscountLabel}
                membershipName={membershipName}
                membershipPrice={membershipPrice}
                membershipOriginalPrice={membershipOriginalPrice}
                // rescue adopters read a line item as who pays: credit the adoption, and say the
                // one number they do pay does not recur
                membershipNote={
                  showRescuePdpRedesign() && !isFreeTrialItem && adoptionPromoApplied
                    ? t('included_with_your_adoption')
                    : undefined
                }
                activationFee={activationFee}
                activationFeeWaived={activationFeeWaived}
                activationFeeNote={showRescuePdpRedesign() ? t('one_time_not_part_of_your_membership') : undefined}
                isFreeTrialItem={isFreeTrialItem}
                removeCartItem={removeCartItem}
                defaultExpanded={defaultExpanded}
                includedItems={includedItems}
                subscriptionPrice={freeTrialSubscriptionPrice}
              />,
            );
          } else {
            addCartItemDetailsToGroup(
              product.category,
              <ProductCartItem
                id={cartItem.cartItemId}
                key={cartItem.cartItemId}
                sku={cartItem.lineItem.sku}
                productImageUrl={imageUrl}
                priceRows={priceRows}
                removeCartItem={removeCartItem}
                isGift={isBundleCartItem(cartItem) ? cartItem.isGift : false}
                quantity={cartItem.quantity}
                flat={isUltraStandalone}
                wholeDollarPrices={isUltraStandalone}
                applyGreyBackground={
                  showEcomCartTest &&
                  (product.category === types.ProductCategory.BAND ||
                    product.category === types.ProductCategory.CHARGING_BASE ||
                    product.category === types.ProductCategory.SUPPLEMENT ||
                    product.category === types.ProductCategory.MINI ||
                    product.category === types.ProductCategory.ULTRA)
                }
                applyBackgroundBlend={
                  showEcomCartTest &&
                  (product.category === types.ProductCategory.BAND ||
                    product.category === types.ProductCategory.CHARGING_BASE)
                }
                productUrl={pdpURLForProduct(product.id)}
                isFreeTrialItem={isFreeTrialItem}
                freeTrialShippingPrice={isFreeTrialItem ? (freeTrialShippingPrice ?? undefined) : undefined}
                freeTrialSubscriptionPrice={freeTrialSubscriptionPrice}
              />,
            );
          }
        }
      } catch (err) {
        invalidCartItemIds.push(cartItem.cartItemId);
      }
    }

    return { cartItemsByGroup, invalidCartItemIds };
  }, [
    adoptionPromoApplied,
    allowRemoveCartItem,
    cart.cartItems,
    cart.couponCode,
    cartItemV2Enabled,
    cartPricing,
    checkoutActions,
    currency,
    defaultExpanded,
    dispatch,
    freeTrialShippingPrice,
    isVerizon,
    makerBandForCartItem,
    priceRowsForCartItem,
    productNameForCartItem,
    productSkuMap,
    showEcomCartTest,
    variantDescriptionForCartItem,
    currencyFormatter,
    wholeNumberCurrencyFormatter,
    t,
  ]);
}

export default function CartItemList({ allowRemoveCartItem, defaultExpanded }: CartItemListProps) {
  const dispatch = useDispatch();
  const { cart, checkoutActions } = useContext(CheckoutContext);
  const { t } = useTranslation();
  const cartItemV2Enabled = useCartItemV2Enabled();
  const resolvedDefaultExpanded =
    defaultExpanded ?? (cartItemV2Enabled ? Object.keys(cart.cartItems).length === 1 : true);
  const { cartItemsByGroup, invalidCartItemIds } = useCartItemsByGroup(allowRemoveCartItem, resolvedDefaultExpanded);

  useEffect(() => {
    if (invalidCartItemIds.length) {
      dispatch(checkoutActions.removeCartItems({ ids: invalidCartItemIds }));
    }
  }, [checkoutActions, dispatch, invalidCartItemIds]);

  const categories = useMemo(
    () => Array.from(cartItemsByGroup.keys()).sort((a, b) => sortOrder(a, t) - sortOrder(b, t)),
    [cartItemsByGroup, t],
  );

  return (
    <div className={styles.main}>
      {categories.map((productCategory, index) => {
        const cartItemsToRender = cartItemsByGroup.get(productCategory) ?? [];
        if (!cartItemsToRender.length) {
          return null;
        }

        return (
          <div className={styles.productGroup} key={`render-group-${index}`}>
            {cartItemsToRender}
          </div>
        );
      })}
    </div>
  );
}
